import {useState, useContext} from 'react';
import {IDEContext} from 'ide/contexts/IDEContext';
import {FaCheck, FaQuestionCircle, FaChevronDown, FaChevronUp, FaExclamationCircle} from 'react-icons/fa';
import Swal from 'sweetalert2';
import {
  ConsoleUnitTest,
  getPostconditionFromDialogue,
  getPreconditionFromDialogueWithIndices,
  isConsoleUnitTest,
} from '../../assignments/models/unitTests/console';
import {inferUnitTestType} from 'ide/UnitTest/runUnitTests';
import { AdvancedConsoleUnitTest } from 'assignments/models/unitTests/advancedConsole';
import { AdvancedAutograderSegmentedOutputToken, getOutputWithVariableSubstitutions, getVariableDescriptionsFromSegmentedOutput, segmentedOutputHasVariableSubstitutions } from 'assignments/unitTest/advancedConsole/advancedConsoleUtils';
import { Link } from 'react-router-dom';
import { useCourseId } from 'hooks/router/useUrlParams';

const LINE_START = '';
const successColor = '#defcee';
const failColor = '#ffc299';
const variableSubstitutionColor = '#6B006B';
const newlineIconFormat = {color: '#999', fontSize: '1.25em'};

export const TestList = () => {
  const ideContext = useContext(IDEContext);
  const {assnData, consoleTestResults, advancedConsoleTestResults, isDiagnostic} = ideContext;
  const unitTests =
    assnData?.unitTests?.unitTestsConsole ?? assnData?.unitTests?.unitTests;
  const assnId = assnData?.metaData?.uid;
  const unitTestType = inferUnitTestType(assnData, ideContext.projectData);

  if (isDiagnostic || !assnId) {
    return <></>;
  }

  const hasAdvancedConsoleUnitTests = assnData?.metaData?.unitTestType == `advanced_console`;

  const shouldShowTestResults =
    unitTests && consoleTestResults && unitTestType === 'console';
  const consoleUnitTests = Array.isArray(unitTests)
    ? unitTests.filter(test => {
        if (!isConsoleUnitTest(test)) {
          console.warn('Ignoring non-console unit test: ', test);
          return false;
        }
        return true;
      })
    : [];
  return (
    <div style={{overflow: 'auto'}}>
      <div className="d-flex" style={{justifyContent: 'space-between'}}>
        <TestInfoButton />
      </div>

      {shouldShowTestResults ? (
        <TestResults
          consoleTestResults={consoleTestResults}
          unitTests={consoleUnitTests}
        />
      ) : hasAdvancedConsoleUnitTests ? (
        <AdvancedConsoleTestResults
          advancedConsoleTestResults={advancedConsoleTestResults || []}
          advancedConsoleUnitTests={assnData?.unitTests?.unitTestsAdvancedConsole}
        />
      ) : (
        <div></div>
      )}
    </div>
  );
};

interface AdvancedConsoleTestResultsProps {
  advancedConsoleTestResults: any[];
  advancedConsoleUnitTests: AdvancedConsoleUnitTest[];
}

const AdvancedConsoleTestResults = ({advancedConsoleTestResults, advancedConsoleUnitTests}: AdvancedConsoleTestResultsProps) => {
  if(advancedConsoleTestResults.length == 0) {
    return <h5 className='mt-2'>Click "Check Correct" to see your test results!</h5>
  }

  return (
    <div>
      {advancedConsoleTestResults.map((result, idx) => {
        const userOutput = getOutputWithVariableSubstitutions(result.rawResult.autograderTrace, 'output');
        const expectedOutput = getOutputWithVariableSubstitutions(result.rawResult.autograderTrace, 'expected_output');

        const userError = result.rawResult?.error ?? [];
        const userInput = result.rawResult?.autograderTrace?.reduce((acc, trace, idx) => {
          if (trace.get('input')) {
            return {
              ...acc,
              [idx]: trace.get('input')
            };
          }
          return acc;
        }, {}) ?? {};

        const expectedInput = result.rawResult?.autograderTrace?.reduce((acc, trace, idx) => {
          if (trace.get('expected_input')) {
            return {
              ...acc,
              [idx]: trace.get('expected_input')
            };
          }
          return acc;
        }, {}) ?? {};

        return (
          <TestView
            userOutput={userOutput}
            userError={userError}
            testOutput={expectedOutput}
            testName={advancedConsoleUnitTests[idx]?.name ?? 'Test'} 
            testOutcome={result.isSuccess ? 'success' : 'failure'}
            cutoff={false}
            key={advancedConsoleUnitTests[idx]?.name}
            testInput={expectedInput}
            description={null}
            userInput={userInput}
            errorMessage={result.rawResult?.message}
            diffOnWhitespace={true}
            isAdvancedConsole={true}
          />
        );
      })}
    </div>
  );
};

interface TestResultsProps {
  consoleTestResults: any[];
  unitTests: ConsoleUnitTest[];
}

const TestResults = ({consoleTestResults, unitTests}: TestResultsProps) => {
  let resultsList = [];
  for (const res of consoleTestResults) {
    if (res.output.length > 50) {
      resultsList.push({
        output: res.output.slice(0, 20),
        cutoff: true,
        error: res.error,
        input: res.input,
      });
    } else {
      resultsList.push({
        output: res.output,
        cutoff: false,
        error: res.error,
        input: res.input,
      });
    }
  }

  return (
    <div>
      {resultsList.map((val, idx) => {
        return (
          <TestView
            userOutput={val.output}
            userInput={val.input ?? {}}
            userError={val.error}
            testOutput={getPostconditionFromDialogue(unitTests[idx])}
            testInput={getPreconditionFromDialogueWithIndices(unitTests[idx])}
            testName={unitTests[idx]?.name}
            testOutcome={consoleTestResults[idx].state ?? 'loading'}
            cutoff={val.cutoff}
            key={unitTests[idx]?.name}
            description={unitTests[idx]?.description ?? ''}
            errorMessage={unitTests[idx]?.description ?? ''}
            diffOnWhitespace={false} // Here to preserve existing behavior
            isAdvancedConsole={false}
          />
        );
      })}
    </div>
  );
};

interface TestViewProps {
  userOutput: string[] | AdvancedAutograderSegmentedOutputToken[][];
  userInput: Record<number, string>;
  userError: string[];
  testOutput: string[] | AdvancedAutograderSegmentedOutputToken[][];
  testInput: Record<number, string>;
  testName: string;
  testOutcome: 'success' | 'failure' | 'loading';
  cutoff: boolean;
  description: string | null;
  errorMessage: string | null;
  diffOnWhitespace?: boolean; // true -- whitespace differences count as differences
  isAdvancedConsole?: boolean;
}

export const TestView = ({
  userOutput,
  userInput,
  userError,
  testOutput,
  testInput,
  testName,
  testOutcome,
  cutoff,
  description,
  errorMessage,
  diffOnWhitespace = false,
  isAdvancedConsole = false
}: TestViewProps) => {
  const isSuccess = testOutcome == 'success';
  const [expanded, setExpanded] = useState(!isSuccess);
  const courseId = useCourseId();
  const backgroundColor = isSuccess
    ? successColor
    : testOutcome == 'failure'
    ? failColor
    : 'lightgray';
  const borderColor = isSuccess
    ? '#198754'
    : testOutcome == 'failure'
    ? '#fd7e14'
    : 'lightgray';
  const resultIcon = isSuccess ? (
    <FaCheck />
  ) : testOutcome == 'failure' ? (
    <span className="badge text-bg-danger">Incomplete</span>
  ) : (
    <></>
  );

  return (
    <div
      style={{
        transition: '0.5s',
        borderRadius: '5px',
        marginTop: '10px',
        border: `1px solid ${borderColor}`,
        fontSize: '12px',
      }}
      className="refCode "
    >
      <div
        onClick={() => setExpanded(!expanded)}
        className="mb-0 p-2 "
        style={{
          cursor: 'pointer',
          borderTopLeftRadius: '5px',
          borderTopRightRadius: '5px',
          borderBottomLeftRadius: expanded ? '0px' : '5px',
          borderBottomRightRadius: expanded ? '0px' : '5px',
          backgroundColor: backgroundColor,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div>
          <code>{testName}</code>{' '}
          <span style={{fontSize: '1.02rem', marginLeft: '5px'}}>
            {resultIcon}
          </span>
        </div>
        <div>
          {expanded ? (
            <FaChevronUp />
          ) : (
            <FaChevronDown />
          )}
        </div>
      </div>
      {expanded ? (
        <div>
          {errorMessage ? (
            <p className="mb-0 border rounded p-1 m-1">
              <strong>Explanation: </strong>
              {errorMessage}
            </p>
          ) : null}
          {
            (isAdvancedConsole 
              && segmentedOutputHasVariableSubstitutions(testOutput as AdvancedAutograderSegmentedOutputToken[][])
            ) &&
            <AdvancedConsoleVariableDisplay
              variables={
                // Since isAdvancedConsole, can safely cast testOutput
                getVariableDescriptionsFromSegmentedOutput(testOutput as AdvancedAutograderSegmentedOutputToken[][])
              } 
              variableSubstitutionColor={variableSubstitutionColor}
            />
          }
          {description && description.length > 0 ? (
            <p className="mb-0 border rounded p-1 m-1">
              <strong>Hints: </strong>
              {description}
            </p>
          ) : null}
          <div>
            {/* "Expected": Test Output/Input is displayed */}
            <DiffMap
              contentList={testOutput}
              diffList={userOutput}
              user={false}
              cutoff={false}
              observedInput={testInput}
              expectedInput={userInput}
              diffOnWhitespace={diffOnWhitespace}
              isAdvancedConsole={isAdvancedConsole}
            />
          </div>
          <div>
            {/* "Observed": User Output/Input is displayed */}
            <DiffMap
              contentList={userOutput}
              diffList={testOutput}
              user={true}
              userError={userError}
              cutoff={cutoff}
              observedInput={userInput}
              expectedInput={testInput}
              diffOnWhitespace={diffOnWhitespace}
              isAdvancedConsole={isAdvancedConsole}
            />
          </div>
          <p className="mb-0 p-1 m-1">
            Not seeing the difference? Check out our <Link to={`/${courseId}/info/diffs`} style={{textDecoration: 'underline'}}>tips</Link>.
          </p>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

interface DiffMapProps {
  contentList: string[] | AdvancedAutograderSegmentedOutputToken[][];
  diffList: string[] | AdvancedAutograderSegmentedOutputToken[][];
  user: boolean;
  cutoff: boolean;
  userError?: string[];
  observedInput: Record<number, string>; // line number -> input
  expectedInput: Record<number, string>; // line number -> input
  diffOnWhitespace?: boolean; // true -- whitespace differences count as differences
  isAdvancedConsole?: boolean;
}

/**
 * @joshdelg I know this is not decomposed. It was already that way, then I added more features and it got worse lol
 * It's on my 2025 Summer TODO for refactor. But right now CiP5 is live so I don't want to risk subtle bugs.
 */
const DiffMap = ({
  contentList,
  diffList,
  user,
  cutoff,
  userError = [],
  observedInput = {},
  expectedInput = {},
  diffOnWhitespace = false,
  isAdvancedConsole = false
}: DiffMapProps) => {
  // The advanced console unit tests use a more advanced data structure for each line (to allow for variable interpolation)
  const contentListText = isAdvancedConsole ? contentList.map(line => line.map(token => token.value).join('')) : contentList;
  const diffListText = isAdvancedConsole ? diffList.map(line => line.map(token => token.value).join('')) : diffList;

  return (
    <div className="p-2 font-monospace">
      <div>
        <p className="mb-0 text-nowrap overflow-scroll">
          <strong>{user ? 'Observed Output' : 'Expected Output'}</strong>
        </p>
        <div style={{overflowY: 'scroll'}}>
          {contentList.map((txt, idx) => {
            const outputDiffers = idx >= diffList.length ||
              (diffOnWhitespace ? 
                (diffListText[idx] != contentListText[idx]) :
                (diffListText[idx].replace(/\s+/g, '') != contentListText[idx].replace(/\s+/g, '')));
            
            const inputDiffers = observedInput[idx] !== expectedInput[idx] && (observedInput[idx] || expectedInput[idx]);

            if (outputDiffers) {
              return (
                <p
                  key={idx}
                  className="overflow-x-auto overflow-y-hidden mb-0"
                  style={{
                    whiteSpace: 'pre',
                    minWidth: '1px',
                    minHeight: '15px',  
                  }}
                >
                  <strong>{LINE_START}</strong>
                  <mark style={{backgroundColor: '#ffc299'}}>
                  {
                    // Advanced Autograder: Text has more advanced data structure to support variable substitutions
                    isAdvancedConsole ? txt.map(
                      token => (
                        token.type == 'substitution' 
                          ? <strong key={token.value} style={{color: variableSubstitutionColor}}>{token.value}</strong> 
                          : <span key={token.value}>{token.value}</span>)
                    ) : txt
                  }
                  </mark>
                  {
                    observedInput[idx] &&
                    inputDiffers ? (
                      <mark style={{backgroundColor: '#ffc299'}}>
                        <strong style={{color: 'blue'}}>
                          {observedInput[idx] ? ' ' + observedInput[idx] : ''}
                        </strong>
                      </mark>
                    ) : (
                      <strong style={{color: 'blue'}}>
                        {observedInput[idx] ? ' ' + observedInput[idx] : ''}
                      </strong>
                    )
                  }
                  <mark style={{backgroundColor: '#ffc299'}}>
                    {((isAdvancedConsole && txt.length == 0) || txt === '') && <strong style={newlineIconFormat}>⏎</strong>}
                  </mark>
                </p>
              );
            }
            return (
              <p key={idx} 
                className="overflow-x-auto overflow-y-hidden mb-0"
                style={{
                  whiteSpace: 'pre',
                  minWidth: '1px',
                  minHeight: '15px',  
                }}
              >
                <strong>{LINE_START}</strong>
                {isAdvancedConsole 
                  ? txt.map(token => token.value).join('')
                  : txt
                }
                {
                  observedInput[idx] && 
                  inputDiffers ? (
                    <mark style={{backgroundColor: '#ffc299'}}>
                      <strong style={{color: 'blue'}}>
                        {observedInput[idx] ? ' ' + observedInput[idx] : ''}
                      </strong>
                    </mark>
                  ) : (
                    <strong style={{color: 'blue'}}>
                      {observedInput[idx] ? ' ' + observedInput[idx] : ''}
                    </strong>
                  )
                }
                {((isAdvancedConsole && txt.length == 0) || txt === '') && <strong style={newlineIconFormat}>⏎</strong>}
              </p>
            );
          })}
          {cutoff ? <p className="mb-0">...</p> : null}
        </div>
      </div>
      {userError.length > 0 ? (
        <div
          className="rounded border mt-1"
          style={{backgroundColor: 'rgba(255, 0, 0, 0.2)'}}
        >
          <p className="mb-0 text-nowrap overflow-scroll bg-light">
            <strong>Errors:</strong>
          </p>
          <div style={{overflowY: 'scroll'}}>
            {userError.map((error, idx) => {
              return (
                <>
                  <code key={idx} className="mb-0">
                    {error}
                  </code>
                  <br />
                </>
              );
            })}
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const TestInfoButton = () => {
  return (
    <div
      onClick={() => {
        Swal.fire({
          title: '<strong>Unit Testing</strong>',
          icon: 'info',
          html: unitTestInfo,
          showCloseButton: true,
          showCancelButton: false,
          focusConfirm: false,
          confirmButtonText: 'Great!',
        });
      }}
      className={`btn radius`}
    >
      <FaQuestionCircle size={24} />
    </div>
  );
};

const unitTestInfo = `
<p>Unit tests are a way to check if your program meets the specifications. When these unit tests are run, your program is given a predetermined set of inputs to see if it behaves as expected.</p>

<p>If there are differences between the expected and actual outputs, those lines will be <mark style="background-color: ${failColor};">highlighted</mark>. It is important to note that unit tests can be very strict, so even small differences can be flagged as errors.</p>`;

const AdvancedConsoleVariableDisplay = ({variables, variableSubstitutionColor}: {variables: Record<string, string>, variableSubstitutionColor: string}) => {
  const courseId = useCourseId();
  
  return (
    <div className="mb-0 border rounded p-1 m-1">
      <p><strong>Output Variables:</strong> Seeing <strong style={{color: variableSubstitutionColor}}> {Object.keys(variables)[0]} </strong>
      in the "Expected Output" <strong style={{textDecoration: "underline"}}>does not</strong> mean that we expect the literal string
      "{Object.keys(variables)[0]}" to appear in your output.
      </p>
      <p>
        Since this problem involves randomness, we don't always know exactly what the output should be.
        In these cases, we use the <strong style={{color: variableSubstitutionColor}}>purple variables</strong> to
        <span style={{fontStyle: 'italic'}}> describe</span> what should be placed there.&nbsp;
        <Link
          to={`/${courseId}/info/autograder/`}
          style={{textDecoration: 'underline'}}
        >
          More info.
        </Link>
        <ul className="mt-2">
        {Object.entries(variables).map(([key, value]) => (
          <li key={key}><strong style={{color: variableSubstitutionColor}}>{key}</strong>: {value}</li>
        ))}
        </ul>
      </p>
    </div>
  );
};